import { Box, DataList, Flex } from '@volue/wave-react';
import React from 'react';

import type { PowerTransformerShortCircuitDetails } from '@/features/execute-calculation-set/overmind/calculation/derived-state/gridElementsDetailsByCalculationType/shortCircuit/ShortCircuitDetails';
import type { ActivePower } from '@/features/execute-calculation-set/overmind/calculation/domain/ActivePower';
import type { ElectricCurrentType } from '@/features/execute-calculation-set/overmind/calculation/domain/ElectricCurrent';
import type { ReactivePower } from '@/features/execute-calculation-set/overmind/calculation/domain/ReactivePower';
import type { Voltage } from '@/features/execute-calculation-set/overmind/calculation/domain/Voltage';
import type { EvaluatedResult } from '@/features/execute-calculation-set/overmind/calculation/evaluation/EvaluatedResult';

import type { SelectedGridElement } from '../../../overmind/calculation';
import { EvaluatedResultComponent } from '../../EvaluatedResultComponent';
import { EvaluatedValue } from '../../MediumVoltageCalculation/LoadFlowSummaryCalculationResults/EvaluatedCalculationResult';
import { LoadFlowDetailsHeader } from './LoadFlowDetailsHeader';

type SelectedPowerTransformer = {
  type: 'SELECTED_GRID_ELEMENT';
  gridElement: SelectedGridElement<'PowerTransformer'>;
};

export const PowerTransformerDetails: React.FunctionComponent<{
  powerTransformer: SelectedPowerTransformer;
}> = ({ powerTransformer }) => {
  return (
    <>
      {powerTransformer.gridElement.loadflow.type === 'LOAD_FLOW_DETAILS' && (
        <Flex flow="column" gap={'spacingXs'}>
          <LoadFlowDetailsHeader
            scenarioName={
              powerTransformer.gridElement.loadflow.details.scenarioName
            }
          />
          {
            <DataList ratio={[3, 2]} className="mHxl">
              {powerTransformer.gridElement.loadflow.details
                .powerTransfomerType === 'ThreeWinding' && (
                <>
                  <PowerTransformerDataListItem
                    value={
                      powerTransformer.gridElement.loadflow.details
                        .primaryVoltage
                    }
                    label={'Primary Voltage'}
                    powerTransformer={powerTransformer}
                  />

                  <PowerTransformerDataListItem
                    value={
                      powerTransformer.gridElement.loadflow.details
                        .secondaryVoltage
                    }
                    label={'Secondary Voltage'}
                    powerTransformer={powerTransformer}
                  />

                  <PowerTransformerDataListItem
                    value={
                      powerTransformer.gridElement.loadflow.details
                        .tertiaryVoltage
                    }
                    label={'Tertiary Voltage'}
                    powerTransformer={powerTransformer}
                  />
                </>
              )}

              {powerTransformer.gridElement.loadflow.details
                .powerTransfomerType === 'TwoWinding' && (
                <>
                  <PowerTransformerDataListItem
                    value={
                      powerTransformer.gridElement.loadflow.details
                        .primaryVoltage
                    }
                    label={'Primary Voltage'}
                    powerTransformer={powerTransformer}
                  />

                  <PowerTransformerDataListItem
                    value={
                      powerTransformer.gridElement.loadflow.details
                        .secondaryVoltage
                    }
                    label={'Secondary Voltage'}
                    powerTransformer={powerTransformer}
                  />
                </>
              )}
              <Flex as={DataList.Row} main="space-between">
                <DataList.Label>{'Rated Capacity'}</DataList.Label>
                <Flex main={'end'} as={DataList.Value}>
                  <EvaluatedResultComponent
                    result={
                      powerTransformer.gridElement.loadflow.details.results
                        .ratedCapacity
                    }
                  />
                </Flex>
              </Flex>
              <Flex as={DataList.Row} main="space-between">
                <DataList.Label>{'Loading'}</DataList.Label>

                <Flex main={'end'} as={DataList.Value}>
                  <EvaluatedResultComponent
                    result={
                      powerTransformer.gridElement.loadflow.details.results
                        .loadingPercent
                    }
                  />
                </Flex>
              </Flex>
              <Flex as={DataList.Row} main="space-between">
                <DataList.Label>{'Available Capacity'}</DataList.Label>
                <Flex main={'end'} as={DataList.Value}>
                  <EvaluatedResultComponent
                    result={
                      powerTransformer.gridElement.loadflow.details.results
                        .availableCapacity
                    }
                  />
                </Flex>
              </Flex>
              <PowerTransformerDataListItem
                value={
                  powerTransformer.gridElement.loadflow.details.sumActivePower
                }
                label={'Sum Active Power'}
                powerTransformer={powerTransformer}
              />
              <PowerTransformerDataListItem
                value={
                  powerTransformer.gridElement.loadflow.details.sumReactivePower
                }
                label={'Sum Reactive Power'}
                powerTransformer={powerTransformer}
              />
            </DataList>
          }
        </Flex>
      )}
      {powerTransformer.gridElement.shortCircuit.type ===
        'SHORT_CIRCUIT_DETAILS' && (
        <Flex flow="column" gap={'spacingXs'}>
          <Box css={{ color: '$colorAccent70', fontWeight: 'bolder' }}>
            {'Short Circuit'}
          </Box>

          {powerTransformer.gridElement.objectType === 'PowerTransformer' &&
            powerTransformer.gridElement.shortCircuit.evaluatedResults
              .objectType === 'PowerTransformer' && (
              <DataList className="mHxl" ratio={[3, 1]}>
                <Phase1Min
                  results={
                    powerTransformer.gridElement.shortCircuit.evaluatedResults
                      .results
                  }
                />
                <Phase1Max
                  results={
                    powerTransformer.gridElement.shortCircuit.evaluatedResults
                      .results
                  }
                />
                <Phase2Min
                  results={
                    powerTransformer.gridElement.shortCircuit.evaluatedResults
                      .results
                  }
                />
                <Phase3Max
                  results={
                    powerTransformer.gridElement.shortCircuit.evaluatedResults
                      .results
                  }
                />
              </DataList>
            )}
        </Flex>
      )}
    </>
  );
};
function PowerTransformerDataListItem(props: {
  powerTransformer: SelectedPowerTransformer;
  label: string;
  value: Voltage | ActivePower | ReactivePower;
}) {
  if (props.powerTransformer.gridElement.loadflow.type !== 'LOAD_FLOW_DETAILS')
    return null;
  return (
    <Flex as={DataList.Row} main="space-between">
      <DataList.Label>{props.label}</DataList.Label>
      <Flex main={'end'} as={DataList.Value}>
        <Box data-testid={props.label} className="dIB">
          {props.value.toString()}
        </Box>
      </Flex>
    </Flex>
  );
}

function EvaluatedDataListItem({
  label,
  value
}: {
  label: string;
  value: EvaluatedResult<ElectricCurrentType>;
}): JSX.Element | null {
  return (
    <Flex as={DataList.Row} main="space-between">
      <DataList.Label>{label}</DataList.Label>
      <Flex main={'end'} as={DataList.Value}>
        <EvaluatedValue value={value.value.toString()} status={value.status} />
      </Flex>
    </Flex>
  );
}

function Phase1Min({
  results
}: {
  results: PowerTransformerShortCircuitDetails['results'];
}): JSX.Element | null {
  return results.phase1Min.type === 'CALCULATION_RESULT_AVAILABLE' ? (
    <EvaluatedDataListItem label="Phase1Min" value={results.phase1Min.result} />
  ) : null;
}

function Phase1Max({
  results
}: {
  results: PowerTransformerShortCircuitDetails['results'];
}): JSX.Element | null {
  return results.phase1Max.type === 'CALCULATION_RESULT_AVAILABLE' ? (
    <EvaluatedDataListItem label="Phase1Max" value={results.phase1Max.result} />
  ) : null;
}

function Phase2Min({
  results
}: {
  results: PowerTransformerShortCircuitDetails['results'];
}): JSX.Element | null {
  return results.phase2Min.type === 'CALCULATION_RESULT_AVAILABLE' ? (
    <EvaluatedDataListItem label="Phase2Min" value={results.phase2Min.result} />
  ) : null;
}

function Phase3Max({
  results
}: {
  results: PowerTransformerShortCircuitDetails['results'];
}): JSX.Element | null {
  return results.phase3Max.type === 'CALCULATION_RESULT_AVAILABLE' ? (
    <EvaluatedDataListItem label="Phase3Max" value={results.phase3Max.result} />
  ) : null;
}
